import React, { useState, ReactElement, useCallback, useEffect, MouseEvent } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import CheckIcon from '@material-ui/icons/Check';
import Popover from '@material-ui/core/Popover';

const useStyles = makeStyles((theme: Theme) => ({
  radio: {
    padding: 0
  },
  radioIcon: {
    width: 48,
    height: 48
  },
  radioIconCurrent: {
    width: 48,
    height: 48,
    color: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4
  },
  radioIconSelected: {
    width: 48,
    height: 48,
    color: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  swatch: {
    width: 192,
    paddingBottom: 24
  },
  colorPicker: {
    width: 192
  }
}));

interface ColorToolProps {
  colors: Array<string>;
  value: string;
  onChange: Function;
  disabled?: boolean;
}

const ColorPicker = (props: ColorToolProps): ReactElement => {
  const { colors, value, onChange, disabled } = props;
  const classes = useStyles();
  const [selectedColor, setSelectedColor] = useState<string>(value);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const handleClick = (event: MouseEvent<HTMLDivElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'color-picker-popover' : undefined;

  useEffect(() => {
    if (selectedColor && selectedColor !== value) onChange(selectedColor);
  }, [selectedColor, onChange, value]);

  const handleColorChange = useCallback(event => setSelectedColor(event.target.value), [
    setSelectedColor
  ]);

  return (
    <div className={classes.swatch}>
      <div
        aria-describedby={id}
        data-testid="color-pick-trigger"
        className={classes.radioIconCurrent}
        style={{ backgroundColor: selectedColor }}
        onClick={!disabled ? handleClick : undefined}
      >
        <CheckIcon style={{ fontSize: 30 }} />
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <div className={classes.colorPicker}>
          {colors.map(color => {
            return (
              <Radio
                key={color}
                className={classes.radio}
                color="default"
                checked={selectedColor === color}
                onChange={handleColorChange}
                value={color}
                aria-labelledby={`tooltip-${color}`}
                icon={<div className={classes.radioIcon} style={{ backgroundColor: color }} />}
                inputProps={{
                  'aria-label': color
                }}
                checkedIcon={
                  <div className={classes.radioIconSelected} style={{ backgroundColor: color }}>
                    <CheckIcon style={{ fontSize: 30 }} />
                  </div>
                }
              />
            );
          })}
        </div>
      </Popover>
    </div>
  );
};

ColorPicker.propTypes = {
  colors: PropTypes.array.isRequired
};

export default ColorPicker;
